import { useEffect } from "react";
import { useMember } from "@/contexts/memberContext";
import { isMemberAnAdmin, isMemberAPracticeManager, isSystemAccount } from "@/core/auth";
import { ChangeBookingFlowProvider, useChangeBookingFlow } from "./ChangeBookingFlowProvider";
import ChangeWhat from "./ChangeWhat";
import Confirmation from "./Confirmation";
import DateTime from "./DateTime";
import Duration from "./Duration";
import DurationConfirmation from "./DurationConfirmation";
import Room from "./Room";
import RoomConfirmation from "./RoomConfirmation";
import SlotBookingRestriction from "./SlotBookingRestriction";
import type { Booking } from "@/types/Booking";
import type { ScreenId } from "./ChangeBookingFlowProvider";

type ChangeBookingFlowContainerProps = {
  booking: Booking | null;
  triggerRefresh?: () => void;
  onClose?: () => void;
  initialChangeType?: "datetime" | "duration" | "room" | null;
};

const ChangeBookingFlowContainer = ({
  booking,
  triggerRefresh,
  onClose,
  initialChangeType,
}: ChangeBookingFlowContainerProps) => {
  return (
    <ChangeBookingFlowProvider booking={booking} initialChangeType={initialChangeType}>
      <ChangeBookingFlowContent triggerRefresh={triggerRefresh} onClose={onClose} />
    </ChangeBookingFlowProvider>
  );
};

const ChangeBookingFlowContent = ({ triggerRefresh, onClose }: Omit<ChangeBookingFlowContainerProps, "booking">) => {
  const {
    currentScreen,
    screenHistory,
    navigateTo,
    booking,
    newStartTime,
    newEndTime,
    newRoom,
  } = useChangeBookingFlow();
  const { member } = useMember();

  // Check if this is a slot booking and if the user can edit it
  const isSlotBooking = booking?.type === "slot";
  const canEditSlotBooking =
    member && (isSystemAccount(member) || isMemberAnAdmin(member) || isMemberAPracticeManager(member));

  // Helper function to determine the first screen
  const getFirstNeededScreen = (): ScreenId => {
    // If it's a slot booking and the user cannot edit it, show the restriction screen
    if (isSlotBooking && !canEditSlotBooking) {
      return "slot-booking-restriction";
    }
    return "change-what";
  };

  // Initialize history when component mounts and history is empty
  useEffect(() => {
    if (screenHistory.length === 0) {
      const firstScreen = getFirstNeededScreen();
      navigateTo(firstScreen);
    }
  }, [screenHistory.length, navigateTo]);

  // Map screen IDs to React components
  const renderScreen = (): React.ReactNode => {
    if (!currentScreen) {
      return null;
    }

    switch (currentScreen) {
      case "slot-booking-restriction":
        return booking ? <SlotBookingRestriction booking={booking} onClose={onClose} /> : null;
      case "change-what":
        return <ChangeWhat onClose={onClose} />;
      case "datetime":
        return <DateTime onClose={onClose} />;
      case "duration":
        return <Duration onClose={onClose} />;
      case "room":
        return <Room onClose={onClose} />;
      case "duration-confirmation":
        return newEndTime && newStartTime ? (
          <DurationConfirmation onClose={onClose} newStartTime={newStartTime} newEndTime={newEndTime} />
        ) : null;
      case "room-confirmation":
        return newRoom ? <RoomConfirmation onClose={onClose} newRoom={newRoom} /> : null;
      case "confirmation":
        return <Confirmation onClose={onClose} triggerRefresh={triggerRefresh} />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full mx-auto" data-testid="change-booking-flow-container">
      {renderScreen()}
    </div>
  );
};

export default ChangeBookingFlowContainer;
